import { useEffect, useState } from "react";
import { get, post } from "../../../redux/ApiHelper";
import { Column } from "../../../interfaces/table.interface";
import TableComponent from "../../../components/table/TableComponent";
import { formatDateTime } from "../../../utils/date&time.util";
import { Tooltip } from "@mui/material";
import { errorToast, successToast } from "../../../utils/toast.util";
import { PayoutInterface } from "../../../interfaces/payout.interface";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { isBeforeTheTimeAndDate } from "../../../utils/helper.util";
import { useRef } from "react"; // at the top if not imported already
import ReReleaseButton from "./RereleaseComp";
import PayoutStatus from "./PayoutStatus";
import PayoutTransactionStatusMore from "./PayoutTransactionStatusMore";

const PayoutsPage = () => {
  const [payoutList, setPayoutList] = useState<PayoutInterface[]>([]);
  const [filteredPayoutList, setFilteredPayoutList] = useState<
    PayoutInterface[]
  >([]);

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [verifyingMap, setVerifyingMap] = useState<Record<string, boolean>>({});
  const { signedAdmin } = useSelector((state: RootState) => state.admin);

  useEffect(() => {
    const queryParams = new URLSearchParams();
    queryParams.set("type", String(signedAdmin.type));
    queryParams.set("adminId", String(signedAdmin.id));
    get(`/admin/payout/all?${queryParams.toString()}`).then((res) => {
      console.log(res.payouts);
      setPayoutList(res.payouts);
      setFilteredPayoutList(res.payouts);
    });
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    const lowerQuery = query.toLowerCase();

    const filteredList = payoutList?.filter((payout) => {
      const payoutId = payout._id || "";
      const teacherId = payout.teacherId._id || "";
      const teacherName = payout.teacherId.name?.toLowerCase() || "";
      const studentName = payout.studentId.name?.toLowerCase() || "";
      const email = payout.teacherId.email?.toLowerCase() || "";
      const payoutDate = formatDateTime(payout?.createdAt);

      return (
        String(payout.withdrawableINR).includes(lowerQuery) ||
        studentName.includes(lowerQuery) ||
        payoutId.includes(lowerQuery) ||
        teacherId.includes(lowerQuery) ||
        email.includes(lowerQuery) ||
        payoutDate.toLowerCase().includes(lowerQuery) ||
        teacherName.includes(lowerQuery)
      );
    });

    setFilteredPayoutList(filteredList);
  };

  const columns: Column[] = [
    { select: "_id", header: "Payout ID", align: "left" },
    { select: "withdrawableINR", header: "INR amount" },
    // { select: "currency", header: "Currency" },
    {
      header: "Student name",
      render: (_value: any, row: PayoutInterface) =>
        row?.studentId.name || "N/A",
    },
    {
      header: "Teacher name",
      render: (_value: any, row: PayoutInterface) =>
        row?.teacherId.name || "N/A",
    },
    {
      header: "Teacher email",
      render: (_value: any, row: PayoutInterface) =>
        row?.teacherId.email || "N/A",
    },
    {
      header: "Teacher phone",
      render: (_value: any, row: PayoutInterface) =>
        row?.teacherId.phoneNumber || "N/A",
    },
    {
      header: "CreatedAt",
      render: (_value: any, row: PayoutInterface) =>
        new Date(row?.createdAt).toLocaleString() || "N/A",
    },
    {
      header: "Requested cashfree",
      render: (_value: any, row: PayoutInterface) => {
        const isCompleted = row?.adminAction === "Pending";
        return (
          <div className="flex items-center">
            <span
              className={`status-badge ${
                !isCompleted
                  ? "bg-green-500"
                  : row?.adminAction === "Pending"
                  ? "bg-yellow-500"
                  : "bg-red-500"
              } text-white px-2 py-1 rounded`}
            >
              {row?.adminAction === "Accepted" ? "Requested" : row?.adminAction}
            </span>{" "}
          </div>
        );
      },
    },
    {
      header: "Action",
      render: (_value: any, row: PayoutInterface) => {
        const isProcessing = verifyingMap[row._id];

        const handleRelease = async () => {
          setVerifyingMap((prev) => ({ ...prev, [row._id]: true }));
          post(`/admin/payout/request-action`, {
            teacherId: row.teacherId._id,
            payoutId: row._id,
            action: "Accept",
          })
            .then((response) => {
              const { payout, message } = response;

              const updatedList = payoutList.map((OldPayout) =>
                OldPayout._id === row._id
                  ? { ...OldPayout, ...payout }
                  : OldPayout
              );
              setPayoutList(updatedList);
              setFilteredPayoutList(updatedList);
              successToast(
                message || "Payment release request sent successfully"
              );
            })
            .catch(() =>
              errorToast("Failed to proceed payment. Please try again.")
            )
            .finally(() =>
              setVerifyingMap((prev) => ({ ...prev, [row._id]: false }))
            );
        };

        return (
          <div className="flex items-center">
            {row?.adminAction === "Pending" && (
              <div className="ml-2">
                <Tooltip
                  title="Click release to procceed the transaction"
                  placement="top"
                >
                  <button
                    className={`text-white px-3 py-1 rounded ${
                      isProcessing
                        ? "bg-blue-300 cursor-not-allowed"
                        : "bg-blue-500 hover:bg-blue-600"
                    }`}
                    onClick={async () => {
                      const confirm = window.confirm(
                        "Are you sure you want to release payment?"
                      );
                      if (confirm) {
                        await handleRelease(); // your POST logic
                      }
                    }}
                    disabled={isProcessing}
                    aria-label="Release Payment"
                  >
                    {isProcessing ? "Releasing..." : "Release"}
                  </button>
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: "Cashfree status",
      render: (_value: any, row: PayoutInterface) => <PayoutStatus row={row} />,
    },
    {
      header: "Rerelease request",
      render: (_value: any, row: PayoutInterface) => (
        <ReReleaseButton row={row} />
      ),
    },
  ];

  return (
    <div className="paymentsPage table-container">
      <div className="title">Payouts</div>
      <div className="search-container mb-4">
        <input
          type="text"
          className="w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700"
          placeholder="Search payments"
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      <TableComponent
        columns={columns.filter((item) => {
          if (
            ((signedAdmin.role === "admin" &&
              signedAdmin.type === "employee") ||
              signedAdmin.type === "franchise") &&
            item.header === "Action"
          ) {
            return false;
          } else {
            return true;
          }
        })}
        data={filteredPayoutList}
        showMore={(row) => (
          <div className="bg-gray-100 p-4 rounded text-sm text-gray-700 text-left space-y-1">
            <PayoutTransactionStatusMore row={row} />
          </div>
        )}
      />
    </div>
  );
};

export default PayoutsPage;
